import { lazy, Suspense } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import ProgressBar from 'react-topbar-progress-indicator';
import { alpha, useTheme } from '@material-ui/core/styles';

// auth screens
import Auth from 'components/Auth/screens/Auth';
import Otp from 'components/Auth/screens/Otp';
import CompleteProfile from 'components/Auth/screens/CompleteProfile';
import ForgetPassword from 'components/Auth/screens/ForgetPassword';

import CustomerEvents from 'components/CustomerOrders';

// templates
import DefaultTemplate from 'templates/DefaultTemplate';

// providers
import { useNavigationDrawerSetState } from 'providers/NavigationDrawerProvider';

// lib
import AdminRoute from 'lib/AdminRoute';
import CustomerRoute from 'lib/CustomerRoute';
import PaymentCallback from 'components/CustomerOrders/components/PaymentCallback';
import CustomerTemplate from 'templates/CustomerTemplate';
import SignupRoute from 'lib/SignupRoute';
import PersonnelRoute from 'lib/PersonnelRoute';

// LazyComponents
const Services = lazy(() => import('components/Services'));
const ServiceDetails = lazy(() => import('components/Services/screens/ServiceDetails'));
const CreateService = lazy(() => import('components/Services/screens/CreateService'));
const DivisionDetails = lazy(() => import('components/Divisions/screens/DivisionDetails'));
const CreateDivision = lazy(() => import('components/Divisions/screens/CreateDivision'));
const CreateBill = lazy(() => import('components/Tools/screens/CreateBill'));
const SellerLanding = lazy(() => import('components/Products'));
const CreateProduct = lazy(() => import('components/Products/screens/CreateProduct'));
const AddProductBatch = lazy(() => import('components/Products/screens/AddProductBatch'));
const Checkout = lazy(() => import('components/Products/screens/Checkout'));
const Orders = lazy(() => import('components/Orders'));
const Customers = lazy(() => import('components/Customers'));
const CustomerDetails = lazy(() => import('components/Customers/screens/CustomerDetails'));
const CreateCustomer = lazy(() => import('components/Customers/screens/CreateCustomer'));
// const NotFound = lazy(() => import('components/NotFound'));
const Profile = lazy(() => import('components/Profile'));
const Settings = lazy(() => import('components/Settings'));
const Invoices = lazy(() => import('components/Invoices'));
const CustomerInvoice = lazy(() => import('components/Invoices/screens/CustomerInvoice'));
const FinalInvoiceDetails = lazy(
  () => import('components/Invoices/screens/FinalInvoiceDetails')
  // import('components/Invoices/screens/PrintInvoice')
);
const AddCredit = lazy(() => import('components/Tools/screens/AddCredit'));
const ConsumeCredit = lazy(() => import('components/Tools/screens/ConsumeCredit'));

const Costs = lazy(() => import('components/Costs'));
const CreateCost = lazy(() => import('components/Costs/screens/CreateCost'));
const Personnel = lazy(() => import('components/Personnel'));
const CreatePersonnel = lazy(() => import('components/Personnel/screens/CreatePersonnel'));
const PersonnelDetails = lazy(() => import('components/Personnel/screens/PersonnelDetails'));
const PersonnelProfile = lazy(() => import('components/Personnel/screens/PersonnelProfile'));


const Discount = lazy(() => import('components/Discount'));
const CreateDiscount = lazy(() => import('components/Discount/screens/CreateDiscount'));
const DiscountDetails = lazy(() => import('components/Discount/screens/DiscountDetails'));

const Blogger = lazy(() => import('components/Blogger'));
const CreateBlogger = lazy(() => import('components/Blogger/screens/CreateBlogger'));
const BloggerDetails = lazy(() => import('components/Blogger/screens/BloggerDetails'));


const MyDetails = lazy(() => import('components/CustomerHistory'));
const MyReservations = lazy(() => import('components/CustomerReservations'));

/**
 * @component Routes
 */
function Routes() {
  const setNavigationOpen = useNavigationDrawerSetState();
  const history = useHistory();
  const theme = useTheme();
  history.listen(() => {
    setNavigationOpen(false);
    window.scrollTo(0, 0);
  });

  ProgressBar.config({
    barColors: {
      0: alpha(theme.palette.primary.main, 0.7),
      '1.0': theme.palette.primary.main,
    },
    shadowBlur: 5,
  });

  return (
    <Switch>
      {/* Authentication Routes */}
      <Route exact component={ForgetPassword} path="/auth/forget-password" />
      <Route exact component={Otp} path="/auth/otp" />
      <Route exact component={Auth} path="/auth" />
      <SignupRoute exact component={CompleteProfile} path="/auth/signup" />

      <Route path={"/customer"}>
      <CustomerTemplate>
          <Suspense fallback={<ProgressBar />}>
            <Switch>
              <CustomerRoute component={CustomerEvents} exact path={'/customer/events'} />
              <CustomerRoute component={PaymentCallback} path={'/customer/paymentcallback'} />
              <CustomerRoute component={MyDetails} path={'/customer/me'} />
              <CustomerRoute component={MyReservations} path={'/customer/reservations'} />
            </Switch>
          </Suspense>
        </CustomerTemplate>
       
      </Route>

      {/* Authentication Routes */}

      <Route>
        <DefaultTemplate>
          <Suspense fallback={<ProgressBar />}>
            <Switch>
              {/* Admin Routes */}

              <Route component={Invoices} exact path={'/invoices'} />
              <Route component={FinalInvoiceDetails} exact path={'/invoices/final/:id'} />
              <Route component={CustomerInvoice} exact path={'/invoices/:id'} />
              <AdminRoute component={Customers} exact path={'(/|/customers)'} />
              <AdminRoute component={Personnel} exact path={'/personnel'} />
              <AdminRoute component={CreatePersonnel} exact path={'/personnel/new'} />
              <AdminRoute component={PersonnelDetails} exact path={'/personnel/:id'} />
              
              <AdminRoute component={Blogger} exact path={'/blogger'} />
              <AdminRoute component={CreateBlogger} exact path={'/blogger/new'} />
              <AdminRoute component={BloggerDetails} exact path={'/blogger/:id'} />

              <AdminRoute component={Discount} exact path={'/discount'} />
              <AdminRoute component={CreateDiscount} exact path={'/discount/new'} />
              <AdminRoute component={DiscountDetails} exact path={'/discount/:id'} />
              <AdminRoute component={Costs} exact path={'/costs'} />
              <AdminRoute component={CreateCost} exact path={'/costs/new'} />
              <AdminRoute component={CreateCustomer} exact path={'/customers/new'} />
              <AdminRoute component={CustomerDetails} exact path={'/customers/:id'} />
              <AdminRoute component={Services} exact path={'/services'} />
              <AdminRoute component={CreateService} exact path={'/services/new'} />
              <AdminRoute component={ServiceDetails} exact path={'/services/:id'} />
              <AdminRoute component={CreateDivision} exact path={'/divisions/new'} />
              <AdminRoute component={DivisionDetails} exact path={'/divisions/:id'} />
              <AdminRoute component={CreateBill} exact path={'/tools/create-bill'} />
              <AdminRoute component={AddCredit} exact path={'/tools/add-credit'} />
              <AdminRoute component={ConsumeCredit} exact path={'/tools/consume-credit'} />
              <AdminRoute component={Settings} exact path={'/settings'} />
              <AdminRoute component={Orders} exact path={'/orders'} />

              {/* Admin Routes */}

              <PersonnelRoute component={PersonnelProfile} exact path={'/me'} />

              {/* DM Routes */}
              <Route component={Profile} exact path={'/profile'} />
              <Route component={SellerLanding} exact path={'/products'} />
              <Route component={Checkout} exact path={'/products/checkout'} />
              <Route component={CreateProduct} exact path={'/products/details'} />
              <Route component={CreateProduct} exact path={'/products/details/:code'} />
              <Route component={AddProductBatch} exact path={'/products/addbatch/:code'} />
              {/* DM Routes */}

              {/* Customer Routes */}

              {/* <Route component={NotFound} /> */}
            </Switch>
          </Suspense>
        </DefaultTemplate>
      </Route>
    </Switch>
  );
}

export default Routes;
